import React, { useState, useEffect } from 'react';
import { TargetSelect } from './TargetSelect';

const USBPolicyScanner = () => {
  const [target, setTarget] = useState('');
  const [scanning, setScanning] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState('form');
  const [scans, setScans] = useState([]);
  const [selectedScan, setSelectedScan] = useState(null);

  useEffect(() => {
    fetchScans();
  }, []);

  const fetchScans = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/scans/usb-policy', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setScans(data);
      }
    } catch (err) {
      console.error('Error fetching USB Policy scans:', err);
    }
  };

  const handleScan = async () => {
    if (!target) {
      setError('Por favor ingrese un dominio o IP');
      return;
    }

    setScanning(true);
    setError(null);
    setResults(null);

    try {
      const token = localStorage.getItem('token');
      
      const payload = {
        name: `USB Policy Assessment - ${target}`,
        host: target,
        port_range: "1-1000",
        target_type: "web",
        description: "Evaluación de controles de medios extraíbles"
      };
      
      const response = await fetch('/api/scans/usb-policy', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Error ejecutando escaneo');
      }

      const data = await response.json();
      setResults(data.results);
      setViewMode('results');
      fetchScans();
    } catch (err) {
      setError(err.message);
    } finally {
      setScanning(false);
    }
  };

  const viewScanDetails = (scan) => {
    setSelectedScan(scan);
    setResults(scan.results);
    setViewMode('results');
  };

  const getRiskColor = (score) => {
    if (score >= 80) return { bg: 'bg-green-50', border: 'border-green-500', text: 'text-green-700', badge: 'bg-green-500' };
    if (score >= 60) return { bg: 'bg-yellow-50', border: 'border-yellow-500', text: 'text-yellow-700', badge: 'bg-yellow-500' };
    if (score >= 40) return { bg: 'bg-orange-50', border: 'border-orange-500', text: 'text-orange-700', badge: 'bg-orange-500' };
    return { bg: 'bg-red-50', border: 'border-red-500', text: 'text-red-700', badge: 'bg-red-500' };
  };

  const getRiskLabel = (riskLevel) => {
    const labels = {
      'LOW': '✅ BAJO RIESGO - Controles Implementados',
      'MEDIUM': '⚠️ RIESGO MEDIO - Controles Parciales',
      'HIGH': '🔴 ALTO RIESGO - Controles Insuficientes',
      'CRITICAL': '🚨 RIESGO CRÍTICO - Sin Controles'
    };
    return labels[riskLevel] || riskLevel;
  };

  const getSeverityColor = (severity) => {
    const colors = {
      'CRITICAL': 'bg-red-100 text-red-800 border-red-300',
      'HIGH': 'bg-orange-100 text-orange-800 border-orange-300',
      'MEDIUM': 'bg-yellow-100 text-yellow-800 border-yellow-300',
      'LOW': 'bg-blue-100 text-blue-800 border-blue-300'
    };
    return colors[severity] || 'bg-gray-100 text-gray-800 border-gray-300';
  };

  return (
    <div className="p-8 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-4xl font-bold text-gray-900 mb-4">
          🔌 Control de Medios Extraíbles (USB/Pendrive)
        </h1>
        <p className="text-lg text-gray-600">
          Evaluación de políticas y controles sobre dispositivos USB y medios removibles
        </p>
      </div>

      {/* View Mode Tabs */}
      <div className="mb-6 flex gap-4">
        <button
          onClick={() => setViewMode('form')}
          className={`px-6 py-3 rounded-lg font-semibold transition-all ${
            viewMode === 'form'
              ? 'bg-blue-600 text-white'
              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          }`}
        >
          🚀 Nuevo Escaneo
        </button>
        <button
          onClick={() => setViewMode('history')}
          className={`px-6 py-3 rounded-lg font-semibold transition-all ${
            viewMode === 'history'
              ? 'bg-blue-600 text-white'
              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          }`}
        >
          📋 Historial ({scans.length})
        </button>
      </div>

      {/* Agent Promotional Banner */}
      <div className="mb-8 bg-gradient-to-r from-purple-50 to-pink-50 border-2 border-purple-200 rounded-lg p-6 shadow-lg">
        <div className="flex items-start">
          <span className="text-4xl mr-4">🔍</span>
          <div className="flex-1">
            <h3 className="text-xl font-bold text-purple-900 mb-2">
              ¿Necesita visibilidad completa de dispositivos USB en su red?
            </h3>
            <p className="text-purple-800 mb-4">
              Este escaneo analiza <strong>políticas públicas</strong> desde internet (sin acceso interno). 
              Para monitoreo en tiempo real de eventos USB, auditoría de dispositivos conectados y alertas automáticas, 
              solicite <strong>CostillaTech Agent</strong>.
            </p>
            <div className="bg-white bg-opacity-70 rounded-lg p-4 mb-4">
              <p className="text-sm font-semibold text-purple-900 mb-2">✨ Con CostillaTech Agent obtendrá:</p>
              <ul className="text-sm text-purple-800 space-y-1">
                <li>• Detección en tiempo real de cada dispositivo USB conectado</li>
                <li>• Historial completo de conexiones de pendrives</li>
                <li>• Alertas automáticas de dispositivos no autorizados</li>
                <li>• Auditoría para cumplimiento ISO 27001 / GDPR</li>
                <li>• 100% seguro: solo lectura, código open source auditable</li>
              </ul>
            </div>
            <a
              href="mailto:contacto@costillatech.com?subject=Consulta CostillaTech Agent - Monitoreo USB"
              className="inline-block bg-purple-600 hover:bg-purple-700 text-white font-semibold py-3 px-6 rounded-lg transition-all duration-200 shadow-md hover:shadow-lg"
            >
              📧 Solicitar Información del Agente
            </a>
            <span className="ml-4 text-sm text-purple-600">
              Disponible para planes Enterprise
            </span>
          </div>
        </div>
      </div>

      {/* Disclaimer */}
      {viewMode === 'form' && (
        <div className="mb-8 bg-blue-50 border-l-4 border-blue-500 p-6 rounded-lg">
          <div className="flex items-start">
            <span className="text-3xl mr-4">ℹ️</span>
            <div>
              <h3 className="text-lg font-semibold text-blue-900 mb-2">Escaneo Pasivo desde Internet</h3>
              <p className="text-blue-800 text-sm mb-2">
                Este análisis evalúa <strong>indicadores externos</strong> de controles de medios extraíbles:
              </p>
              <ul className="text-blue-800 text-sm space-y-1 list-disc list-inside">
                <li>Políticas públicas de seguridad sobre USB</li>
                <li>Certificaciones ISO 27001 (Control A.8.3.1)</li>
                <li>Indicadores de soluciones DLP (Data Loss Prevention)</li>
                <li>Presencia de MDM/EMM en stack tecnológico</li>
              </ul>
              <p className="text-blue-800 text-sm mt-2">
                <strong>No requiere acceso interno ni credenciales.</strong> Para auditoría interna de dispositivos USB 
                reales, considere CostillaTech Agent.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Scan Form */}
      {viewMode === 'form' && (
        <div className="bg-white rounded-lg shadow-lg p-6 mb-8">
          <TargetSelect
            label="Dominio o IP a Escanear"
            value={target}
            onChange={setTarget}
            types={['domain', 'ip']}
            disabled={scanning}
            testid="usb-target-select"
          />
          <div className="flex gap-4 mt-4">
            <button
              onClick={handleScan}
              disabled={scanning}
              className="px-8 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors duration-200"
            >
              {scanning ? '⏳ Escaneando...' : '🚀 Iniciar Escaneo'}
            </button>
          </div>
          {error && (
            <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
              ❌ {error}
            </div>
          )}
        </div>
      )}

      {/* Loading State */}
      {scanning && (
        <div className="bg-white rounded-lg shadow-lg p-12 text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600 mx-auto mb-4"></div>
          <p className="text-xl font-semibold text-gray-900">Analizando controles de medios extraíbles...</p>
          <p className="text-gray-600 mt-2">Evaluando políticas, certificaciones y controles técnicos</p>
        </div>
      )}

      {/* History View */}
      {viewMode === 'history' && (
        <div className="bg-white rounded-lg shadow-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Historial de Evaluaciones de Políticas USB
          </h3>

          {scans.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <p>No hay escaneos registrados aún.</p>
              <button
                onClick={() => setViewMode('form')}
                className="mt-4 text-blue-600 hover:underline"
              >
                Crear primer escaneo →
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              {scans.map((scan) => {
                const usbScore = scan.results?.usb_policy_score || 0;
                const riskLevel = scan.results?.risk_level || 'UNKNOWN';
                return (
                  <div
                    key={scan.id}
                    className="border border-gray-200 rounded-lg p-4 hover:border-blue-400 cursor-pointer transition"
                    onClick={() => viewScanDetails(scan)}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="font-medium text-gray-900">{scan.target}</h4>
                        <p className="text-sm text-gray-500">
                          {new Date(scan.created_at).toLocaleString('es-AR')}
                        </p>
                      </div>
                      <div className="text-right">
                        <div className={`text-2xl font-bold ${getRiskColor(usbScore).text}`}>
                          {usbScore}/100
                        </div>
                        <p className={`text-xs px-2 py-1 rounded ${getRiskColor(usbScore).bg}`}>
                          {getRiskLabel(riskLevel)}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Results View */}
      {viewMode === 'results' && results && (
        <div className="space-y-8">
          {/* Back Button */}
          <button
            onClick={() => setViewMode('history')}
            className="text-blue-600 hover:text-blue-800 font-semibold"
          >
            ← Volver al historial
          </button>

          {/* Score Principal */}
          <div className={`rounded-lg shadow-xl p-8 border-2 ${getRiskColor(results.usb_policy_score).border} ${getRiskColor(results.usb_policy_score).bg}`}>
            <div className="text-center">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Nivel de Control de Medios Extraíbles
              </h2>
              <div className={`text-9xl font-bold mb-4 ${getRiskColor(results.usb_policy_score).text}`}>
                {results.usb_policy_score}
                <span className="text-4xl text-gray-500">/100</span>
              </div>

              {/* Progress Bar */}
              <div className="w-full bg-gray-200 rounded-full h-8 mb-6">
                <div
                  className={`h-8 rounded-full transition-all duration-500 ${getRiskColor(results.usb_policy_score).badge} flex items-center justify-end pr-4`}
                  style={{ width: `${results.usb_policy_score}%` }}
                >
                  <span className="text-white font-bold text-sm">{results.usb_policy_score}%</span>
                </div>
              </div>

              {/* Risk Badge */}
              <div className={`inline-block px-8 py-4 rounded-full font-bold text-2xl ${getRiskColor(results.usb_policy_score).badge} text-white shadow-lg`}>
                {getRiskLabel(results.risk_level)}
              </div>
            </div>
          </div>

          {/* Detailed Results */}
          {results.detailed_results && (
            <div className="bg-white rounded-lg shadow-lg p-6">
              <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                <span className="text-3xl mr-3">📋</span>
                Resultados del Análisis
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Public Policies */}
                {results.detailed_results.public_policies && (
                  <div className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center mb-2">
                      <span className="text-2xl mr-2">📄</span>
                      <span className="font-semibold text-gray-900">Políticas Públicas</span>
                    </div>
                    <p className={`text-lg font-bold ${results.detailed_results.public_policies.usb_policy_found ? 'text-green-600' : 'text-red-600'}`}>
                      {results.detailed_results.public_policies.usb_policy_found ? 'Detectadas' : 'No Detectadas'}
                    </p>
                    <p className="text-sm text-gray-600 mt-1">
                      Confianza: {results.detailed_results.public_policies.confidence || 'Baja'}
                    </p>
                  </div>
                )}

                {/* Certifications */}
                {results.detailed_results.certifications && (
                  <div className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center mb-2">
                      <span className="text-2xl mr-2">🏆</span>
                      <span className="font-semibold text-gray-900">Certificaciones</span>
                    </div>
                    <p className={`text-lg font-bold ${results.detailed_results.certifications.iso27001 ? 'text-green-600' : 'text-red-600'}`}>
                      ISO 27001: {results.detailed_results.certifications.iso27001 ? 'Verificada' : 'No Verificada'}
                    </p>
                    {results.detailed_results.certifications.certifications_found && results.detailed_results.certifications.certifications_found.length > 0 && (
                      <p className="text-sm text-gray-600 mt-1">
                        Encontradas: {results.detailed_results.certifications.certifications_found.join(', ')}
                      </p>
                    )}
                  </div>
                )}

                {/* DLP Presence */}
                {results.detailed_results.dlp_presence && (
                  <div className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center mb-2">
                      <span className="text-2xl mr-2">🛡️</span>
                      <span className="font-semibold text-gray-900">Solución DLP</span>
                    </div>
                    <p className={`text-lg font-bold ${results.detailed_results.dlp_presence.dlp_detected ? 'text-green-600' : 'text-red-600'}`}>
                      {results.detailed_results.dlp_presence.dlp_detected ? 'Detectada' : 'No Detectada'}
                    </p>
                    {results.detailed_results.dlp_presence.dlp_vendor && results.detailed_results.dlp_presence.dlp_vendor !== 'unknown' && (
                      <p className="text-sm text-gray-600 mt-1">
                        Vendor: {results.detailed_results.dlp_presence.dlp_vendor}
                      </p>
                    )}
                  </div>
                )}

                {/* Technology Stack */}
                {results.detailed_results.technology_stack && (
                  <div className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center mb-2">
                      <span className="text-2xl mr-2">💻</span>
                      <span className="font-semibold text-gray-900">Stack Tecnológico</span>
                    </div>
                    <p className={`text-lg font-bold ${results.detailed_results.technology_stack.mdm_detected ? 'text-green-600' : 'text-red-600'}`}>
                      MDM: {results.detailed_results.technology_stack.mdm_detected ? 'Detectado' : 'No Detectado'}
                    </p>
                    <p className="text-sm text-gray-600 mt-1">
                      EDR: {results.detailed_results.technology_stack.edr_indicators ? 'Sí' : 'No'}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Vulnerabilities */}
          {results.vulnerabilities && results.vulnerabilities.length > 0 && (
            <div className="bg-white rounded-lg shadow-lg p-6">
              <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                <span className="text-3xl mr-3">⚠️</span>
                Vulnerabilidades y Brechas de Seguridad ({results.vulnerabilities.length})
              </h3>
              <div className="space-y-4">
                {results.vulnerabilities.map((vuln, index) => (
                  <div key={index} className={`border-2 rounded-lg p-6 ${getSeverityColor(vuln.severity)}`}>
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <span className={`px-3 py-1 rounded-full text-xs font-bold ${getSeverityColor(vuln.severity)}`}>
                            {vuln.severity}
                          </span>
                          <h4 className="text-lg font-bold text-gray-900">{vuln.title}</h4>
                        </div>
                        <p className="text-gray-700 whitespace-pre-line mb-4">{vuln.description}</p>
                        
                        {vuln.recommendation && (
                          <div className="bg-white bg-opacity-50 rounded-lg p-4 mb-3">
                            <p className="text-sm font-semibold text-gray-900 mb-1">💡 Recomendación:</p>
                            <p className="text-sm text-gray-800">{vuln.recommendation}</p>
                          </div>
                        )}

                        {/* Compliance References */}
                        <div className="flex flex-wrap gap-2 mt-3">
                          {vuln.iso27001 && (
                            <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                              ISO 27001: {vuln.iso27001}
                            </span>
                          )}
                          {vuln.pci_dss && (
                            <span className="text-xs bg-purple-100 text-purple-800 px-2 py-1 rounded">
                              PCI DSS: {vuln.pci_dss}
                            </span>
                          )}
                          {vuln.gdpr && (
                            <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">
                              GDPR: {vuln.gdpr}
                            </span>
                          )}
                        </div>

                        {/* Cost and Timeline */}
                        {(vuln.cost || vuln.timeline) && (
                          <div className="flex gap-4 mt-3 text-sm">
                            {vuln.cost && (
                              <div className="text-gray-600">
                                <strong>Costo:</strong> {vuln.cost}
                              </div>
                            )}
                            {vuln.timeline && (
                              <div className="text-gray-600">
                                <strong>Timeline:</strong> {vuln.timeline}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Recommendations */}
          {results.recommendations && results.recommendations.length > 0 && (
            <div className="bg-white rounded-lg shadow-lg p-6">
              <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                <span className="text-3xl mr-3">💡</span>
                Recomendaciones Priorizadas
              </h3>
              <div className="space-y-4">
                {results.recommendations.map((rec, index) => {
                  const priorityColors = {
                    'CRITICAL': 'bg-red-500',
                    'HIGH': 'bg-orange-500',
                    'MEDIUM': 'bg-yellow-500',
                    'LOW': 'bg-blue-500'
                  };
                  return (
                    <div key={index} className="border-2 border-gray-200 rounded-lg p-6">
                      <div className="flex items-start gap-4">
                        <span className={`flex-shrink-0 w-10 h-10 ${priorityColors[rec.priority]} text-white rounded-full flex items-center justify-center font-bold text-lg`}>
                          {index + 1}
                        </span>
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <span className={`px-3 py-1 ${priorityColors[rec.priority]} text-white rounded-full text-xs font-bold`}>
                              {rec.priority}
                            </span>
                            <h4 className="text-xl font-bold text-gray-900">{rec.title}</h4>
                          </div>
                          <p className="text-gray-700 mb-4">{rec.description}</p>

                          {/* Steps */}
                          {rec.steps && rec.steps.length > 0 && (
                            <div className="bg-gray-50 rounded-lg p-4 mb-4">
                              <p className="font-semibold text-gray-900 mb-2">Pasos a seguir:</p>
                              <ul className="space-y-1 text-sm text-gray-800">
                                {rec.steps.map((step, i) => (
                                  <li key={i}>{step}</li>
                                ))}
                              </ul>
                            </div>
                          )}

                          {/* Metrics */}
                          <div className="grid grid-cols-3 gap-4">
                            {rec.timeline && (
                              <div className="bg-blue-50 rounded-lg p-3">
                                <p className="text-xs text-blue-600 font-semibold mb-1">Timeline</p>
                                <p className="text-sm font-bold text-blue-900">{rec.timeline}</p>
                              </div>
                            )}
                            {rec.cost && (
                              <div className="bg-green-50 rounded-lg p-3">
                                <p className="text-xs text-green-600 font-semibold mb-1">Costo</p>
                                <p className="text-sm font-bold text-green-900">{rec.cost}</p>
                              </div>
                            )}
                            {rec.impact && (
                              <div className="bg-purple-50 rounded-lg p-3">
                                <p className="text-xs text-purple-600 font-semibold mb-1">Impacto</p>
                                <p className="text-sm font-bold text-purple-900">{rec.impact}</p>
                              </div>
                            )}
                          </div>

                          {/* Compliance */}
                          {rec.compliance && rec.compliance.length > 0 && (
                            <div className="mt-3 flex flex-wrap gap-2">
                              {rec.compliance.map((comp, i) => (
                                <span key={i} className="text-xs bg-indigo-100 text-indigo-800 px-2 py-1 rounded">
                                  {comp}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Compliance Gaps */}
          {results.compliance_gaps && results.compliance_gaps.length > 0 && (
            <div className="bg-gradient-to-br from-red-50 to-orange-50 rounded-lg shadow-lg p-6 border-2 border-red-200">
              <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                <span className="text-3xl mr-3">📊</span>
                Brechas de Cumplimiento Detectadas
              </h3>
              <p className="text-gray-700 mb-4">
                Su organización presenta gaps en los siguientes estándares de seguridad:
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {results.compliance_gaps.map((gap, index) => (
                  <div key={index} className="bg-white rounded-lg p-3 border border-red-200">
                    <p className="text-sm text-red-900 font-medium">🔴 {gap}</p>
                  </div>
                ))}
              </div>
              <div className="mt-4 bg-white bg-opacity-70 rounded-lg p-4">
                <p className="text-sm text-gray-800">
                  <strong>Nota:</strong> Estas brechas pueden afectar la decisión de aseguradoras sobre cobertura cibernética 
                  y resultar en primas más altas o rechazo de póliza.
                </p>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default USBPolicyScanner;
